import { MainLayout } from '../components/MainLayout';
import { useNavigate } from 'react-router-dom';
import { useCallHistoryStore } from '../store/callHistoryStore';
import { useVaultStore } from '../store/vaultStore';
import { useAuthStore } from '../store/authStore';
import { useEffect, useMemo, useState, type MouseEvent, type TouchEvent } from 'react';
import { getLocale, useI18n } from '../i18n';
import { useCall } from '../hooks/useCall';
import { useServerColorStore } from '../store/serverColorStore';
import { resolveServerColorClass } from '../utils/serverColor';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ensureIdentitySelected, ensureServerSelected } from '../services/activeDevice';
import { showError, showWarning } from '../utils/feedback';
import { canDirectGuestStartCalls, getDirectGuestRoleLabel, isDirectGuestContact } from '../utils/directGuest';
import { shouldShowCachedServerState, useServerHealthStore } from '../store/serverHealthStore';
import { usePublishedIdentitiesStore } from '../store/publishedIdentitiesStore';
import { navigateBackOrFallback } from '../utils/navigationBack';
import { findContactForIdentityContext } from '../utils/contactHelpers';

export function CallsPage() {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const { events, markMissedCallsSeen } = useCallHistoryStore();
  const { vaultData } = useVaultStore();
  const servers = useAuthStore((state) => Object.values(state.identities));
  const serverHealthById = useServerHealthStore((state) => state.byServerId);
  const { initiateCall, initiateCallToIdentity } = useCall();
  const serverColorById = useServerColorStore((state) => state.byServerId);
  const publishedByServerId = usePublishedIdentitiesStore((state) => state.byServerId);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);
  const [addUnknownDialog, setAddUnknownDialog] = useState<{
    remoteIdentityId: string;
    serverUrl: string;
    publicKey: string;
    serverId?: string;
    serverIdentityName?: string;
  } | null>(null);

  const releaseActionButtonFocus = (
    event: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>
  ) => {
    event.currentTarget.blur();
  };

  useEffect(() => {
    // Visiting the Calls page counts as "seen" for missed calls.
    markMissedCallsSeen();
  }, [markMissedCallsSeen]);

  const contacts = useMemo(() => Object.values(vaultData?.contacts || {}), [vaultData]);

  const resolveServerLabel = (event: (typeof events)[0]) => {
    // Prefer current client-known server name (human-friendly) over anything stored in history.
    const identity = servers.find((s) => s.serverId === event.serverId);
    const label = (identity?.serverName || identity?.serverId || '').trim();
    if (label) return label;

    // Fall back to history value, but avoid showing URL-looking strings.
    const fallback = (event.serverName || '').trim();
    if (fallback && !/^https?:\/\//i.test(fallback)) return fallback;

    return (event.serverId || '').trim() || t('common.server');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const visibleCachedServerCount = useMemo(() => {
    const uniqueServerIds = new Set(events.map((event) => event.serverId).filter(Boolean));
    let count = 0;
    for (const serverId of uniqueServerIds) {
      if (shouldShowCachedServerState(serverHealthById[serverId], nowMs)) count += 1;
    }
    return count;
  }, [events, nowMs, serverHealthById]);

  const handleCallFromHistory = async (event: (typeof events)[0], opts?: { withVideo?: boolean }) => {
    if (event.isTemporaryLinkCall === true) {
      showWarning(t('calls.callbackBlockedTemporaryLink'));
      return;
    }

    if (busyEventId) return;
    setBusyEventId(event.id);
    try {
      // Ensure WS targets the correct server for this call log entry.
      const eventLocalIdentityId = resolveLocalIdentityIdForEvent(event);
      if (eventLocalIdentityId) {
        ensureIdentitySelected(eventLocalIdentityId);
      } else {
        ensureServerSelected(event.serverId);
      }

      const contact = resolveContactForEvent(event);
      const resolvedIdentityId = resolveIdentityIdForEvent(event);

      if (contact && resolvedIdentityId) {
        await initiateCall(contact.contactId, opts?.withVideo === true, resolvedIdentityId);
        return;
      }

      // Unknown contact: allow callback without adding to contacts.
      const remoteIdentityId = (event.remoteIdentityId || '').trim();
      if (!remoteIdentityId) {
        showError(t('calls.errors.insufficientContactData'));
        return;
      }

      const serverIdentityName = (event.remoteIdentityName || event.remoteDisplayName || '').trim();
      await initiateCallToIdentity({
        remoteIdentityId,
        remoteDisplayName: serverIdentityName || remoteIdentityId,
        remoteIdentityPublicKey: event.remotePublicKey,
        serverId: event.serverId,
        serverUrl: event.serverUrl,
        serverIdentityName,
        withVideo: opts?.withVideo === true
      });
    } finally {
      setBusyEventId(null);
    }
  };

  const resolveLocalIdentityIdForEvent = (event: (typeof events)[0]) => {
    const direct = (event.localIdentityId || '').trim();
    if (direct) return direct;

    const serverId = (event.serverId || '').trim();
    if (!serverId) return null;
    const identity = servers.find((s) => (s.serverId || '').trim() === serverId);
    return identity?.identityId || null;
  };

  const resolveContactForEvent = (event: (typeof events)[0]) => {
    if (!vaultData) return null;

    const remoteKey = (event.remotePublicKey?.value || '').trim();
    const remoteId = (event.remoteIdentityId || '').trim();

    const matchingContacts = contacts.filter((contact) => {
      const has = contact.identities.some((i) => {
        if (i.serverId !== event.serverId) return false;

        const iId = (i.identityId || '').trim();
        const iKey = (i.identityPublicKey.value || '').trim();

        if (remoteKey && iKey && iKey === remoteKey) return true;
        if (remoteId && iId && iId === remoteId) return true;

        return false;
      });
      return has;
    });
    const remoteIdentityId = (event.remoteIdentityId || '').trim();
    return remoteIdentityId
      ? findContactForIdentityContext(matchingContacts, remoteIdentityId, resolveLocalIdentityIdForEvent(event) || undefined)
      : matchingContacts[0] || null;
  };

  const resolveIdentityIdForEvent = (event: (typeof events)[0]) => {
    if (!vaultData) return null;
    const contact = resolveContactForEvent(event);
    if (!contact) return null;

    const remoteKey = (event.remotePublicKey?.value || '').trim();
    const remoteId = (event.remoteIdentityId || '').trim();

    const identity = contact.identities.find((i) => {
      if (i.serverId !== event.serverId) return false;

      const iId = (i.identityId || '').trim();
      const iKey = (i.identityPublicKey?.value || '').trim();

      if (remoteKey && iKey && iKey === remoteKey) return true;
      if (remoteId && iId && iId === remoteId) return true;
      return false;
    });

    return identity?.identityId || null;
  };

  const renderDirectionIcon = (direction: 'incoming' | 'outgoing') => {
    if (direction === 'incoming') {
      // Arrow down-left
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7L7 19M7 19H15M7 19V11" />
        </svg>
      );
    }

    // Arrow up-right
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19L17 7M17 7H9M17 7V15" />
      </svg>
    );
  };

  const getOutcome = (event: (typeof events)[0]) => {
    if (event.status === 'answered' || (event.status === 'ended' && !!event.durationSeconds)) return 'accepted' as const;
    if (event.status === 'rejected') return 'declined' as const;
    if (event.status === 'ringing') return 'ringing' as const;
    // Treat ended/failed as "missed" for compact UI.
    return 'missed' as const;
  };

  const renderOutcomeIcon = (event: (typeof events)[0]) => {
    const outcome = getOutcome(event);

    if (outcome === 'declined') {
      // X
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
        </svg>
      );
    }

    if (outcome === 'missed') {
      // Missed: dash
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
        </svg>
      );
    }

    return null;
  };

  const temporaryMemberships = useAuthStore((state) => state.temporaryMemberships);
  const temporaryEntries = Object.values(temporaryMemberships);
  const temporaryOnlyMode = servers.length === 0 && temporaryEntries.length > 0;
  const hasServers = servers.length > 0;

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return t('calls.time.today', {
        time: date.toLocaleTimeString(getLocale(language), { hour: '2-digit', minute: '2-digit' })
      });
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isYesterday) {
      return t('calls.time.yesterday', {
        time: date.toLocaleTimeString(getLocale(language), { hour: '2-digit', minute: '2-digit' })
      });
    }

    return date.toLocaleDateString(getLocale(language), {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (durationSeconds?: number) => {
    if (!durationSeconds || durationSeconds < 1) return null;
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    const seconds = durationSeconds % 60;

    if (hours > 0) {
      return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
    }

    return [minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  };

  const handleOpenHistoryItem = (event: (typeof events)[0]) => {
    if (!vaultData) {
      showError(t('calls.errors.vaultNotLoaded'));
      return;
    }

    const contact = resolveContactForEvent(event);

    if (!contact) return;

    if (!contact) {
      showError(t('calls.errors.contactUnavailable'));
      return;
    }

    navigate(`/contacts/${contact.contactId}`, { state: { from: 'calls' } });
  };

  const handleToggleActions = (event: (typeof events)[0]) => {
    const contact = resolveContactForEvent(event);
    if (!contact) {
      handleAddUnknownToContacts(event);
      return;
    }
    setExpandedEventId((prev) => (prev === event.id ? null : event.id));
  };

  const resolvePublicKeyForUnknownEvent = (event: (typeof events)[0]) => {
    if (event.remotePublicKey?.value) return event.remotePublicKey;
    return publishedByServerId[event.serverId]?.find((p) => p.identityId === event.remoteIdentityId)?.publicKey || null;
  };

  const resolveServerUrlForEvent = (event: (typeof events)[0]) => {
    const direct = (event.serverUrl || '').trim();
    if (direct) return direct;

    const serverId = (event.serverId || '').trim();
    if (!serverId) return '';
    return (servers.find((s) => (s.serverId || '').trim() === serverId)?.serverUrl || '').trim();
  };

  const handleAddUnknownToContacts = (event: (typeof events)[0]) => {
    const resolved = resolveContactForEvent(event);
    if (resolved) {
      navigate(`/contacts/${resolved.contactId}`, { state: { from: 'calls' } });
      return;
    }

    // The verified key from the call is authoritative. Published identities are
    // only a fallback for older local history entries that were saved without it.
    const publicKey = resolvePublicKeyForUnknownEvent(event);
    const serverUrl = resolveServerUrlForEvent(event);

    if (!serverUrl || !publicKey?.value) {
      showError(t('calls.errors.insufficientContactData'));
      return;
    }

    setAddUnknownDialog({
      remoteIdentityId: event.remoteIdentityId || '',
      serverUrl,
      publicKey: publicKey.value,
      serverId: event.serverId,
      serverIdentityName: event.remoteIdentityName || undefined
    });
  };

  return (
    <MainLayout>
      <div className="page-shell flex h-full min-h-0 flex-col py-2 sm:py-3">
        <ConfirmDialog
          open={!!addUnknownDialog}
          title={t('common.addToContacts')}
          description={t('calls.addUnknownDialog.description')}
          cancelText={t('common.cancel')}
          confirmText={t('calls.addUnknownDialog.confirm')}
          onCancel={() => setAddUnknownDialog(null)}
          onConfirm={() => {
            const d = addUnknownDialog;
            if (!d) return;

            const payload = {
              v: 4,
              identityId: d.remoteIdentityId,
              serverUrl: d.serverUrl,
              publicKey: d.publicKey,
              identityName: d.serverIdentityName
            };

            setAddUnknownDialog(null);
            navigate('/contacts/add', {
              state: {
                prefillContactPayload: payload,
                from: 'calls'
              }
            });
          }}
        >
          {addUnknownDialog ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-600">{t('addContact.identityIdLabel')}</div>
                <div className="mt-1 text-sm font-mono text-gray-900 break-all">
                  {addUnknownDialog.remoteIdentityId || t('common.unknown')}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-600">{t('common.server')}</div>
                <div className="mt-1 text-sm text-gray-900 break-all">{addUnknownDialog.serverUrl}</div>
              </div>
              <div className="text-xs text-gray-600">
                {t('calls.addUnknownDialog.hint')}
              </div>
            </div>
          ) : null}
        </ConfirmDialog>

        <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <button
              type="button"
              onClick={() => navigateBackOrFallback(navigate, '/contacts')}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-[color:var(--text-soft)] transition-colors hover:bg-white/55 hover:text-[color:var(--text-strong)]"
              aria-label={t('common.back')}
              title={t('common.back')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold tracking-[-0.02em] text-[color:var(--text-strong)] sm:text-xl">
              {t('nav.calls')}
            </h1>
          </div>
          <button
            type="button"
            onClick={() => navigate('/scan', { state: { returnTo: '/calls' } })}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[1.1rem] border border-[color:var(--border-soft)] bg-white/80 text-[color:var(--text-strong)] shadow-[0_10px_24px_rgba(53,38,27,0.05)] transition-colors hover:bg-white"
            aria-label={t('common.scanQr')}
            title={t('common.scanQr')}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7V5a2 2 0 012-2h2M3 17v2a2 2 0 002 2h2M17 3h2a2 2 0 012 2v2M19 17v2a2 2 0 01-2 2h-2" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12h10" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {visibleCachedServerCount > 0 ? (
            <div className="mb-4 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-700 w-fit">
              {t('servers.statusCached')}
            </div>
          ) : null}

          {/* Zero state: temporary only mode */}
          {temporaryOnlyMode && (
            <div className="card-elevated">
              <div className="section-eyebrow mb-2">{t('calls.temporaryMode.title', { serverName: temporaryEntries[0]?.serverUrl || '' })}</div>
              <p className="text-[color:var(--text-muted)] mb-4">{t('calls.temporaryMode.description')}</p>
              <button
                type="button"
                onClick={() => navigate(`/temporary-access/${encodeURIComponent(temporaryEntries[0].enrollmentId)}`)}
                className="btn btn-primary"
              >
                {t('calls.temporaryMode.requestPermanentAccess')}
              </button>
            </div>
          )}

          {/* Zero state: no servers */}
          {!hasServers && !temporaryOnlyMode && (
            <div className="card-elevated text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </div>
              <h2 className="mb-2 text-xl font-semibold text-[color:var(--text-strong)]">{t('calls.welcomeTitle')}</h2>
              <p className="mb-6 text-[color:var(--text-muted)]">
                {t('calls.welcomeDescriptionLine1')}<br />
                {t('calls.welcomeDescriptionLine2')}
              </p>
              <button
                onClick={() => navigate('/register')}
                className="btn btn-primary"
              >
                {t('common.connectToServer')}
              </button>
              <p className="mt-4 text-sm text-[color:var(--text-soft)]">
                {t('calls.welcomeFooterLine1')}<br />
                {t('calls.welcomeFooterLine2')}
              </p>
            </div>
          )}

          {/* Main state: has servers */}
          {hasServers && (
            <>
              {/* Call history */}
              <div className="card p-0">
              {events.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="section-eyebrow mb-2">{t('calls.recentEyebrow')}</div>
                  <p className="text-base font-medium text-[color:var(--text-strong)]">{t('calls.emptyTitle')}</p>
                  {t('calls.emptyDescription') ? (
                    <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                      {t('calls.emptyDescription')}
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="divide-y divide-[color:var(--border-soft)]">
                  {events.map((event) => (
                    (() => {
                      const resolved = resolveContactForEvent(event);
                      const displayName = resolved?.displayName || '';
                      const directGuestBadge = isDirectGuestContact(resolved) ? getDirectGuestRoleLabel(resolved) : null;
                      const isKnown = !!resolved;
                      const resolvedIdentityId = resolveIdentityIdForEvent(event);
                      const isTemporaryLinkCall = event.isTemporaryLinkCall === true;
                      const callDuration = formatDuration(event.durationSeconds);
                      const linkCallLabel = t('calls.linkCallLabel');
                      const allowsCalls = canDirectGuestStartCalls(resolved);
                      const canRetryCall = !isTemporaryLinkCall
                        && allowsCalls
                        && Boolean((isKnown && resolvedIdentityId) || (!isKnown && (event.remoteIdentityId || '').trim()));
                      const retryDisabledTitle = isTemporaryLinkCall
                        ? t('calls.callbackBlockedTemporaryLink')
                        : !allowsCalls
                          ? t('contacts.callsUnavailable')
                          : t('common.call');
                      const serverIdentityName = (event.remoteIdentityName || event.remoteDisplayName || '').trim();
                      const serverLabel = resolveServerLabel(event);
                      const useServerIdentityAsPrimary = !isKnown;
                      const primaryDisplayName = isTemporaryLinkCall
                        ? linkCallLabel
                        : useServerIdentityAsPrimary
                        ? (serverIdentityName || t('common.unknown'))
                        : (displayName || event.remoteIdentityId);
                      const showCompactIdentityId = !isTemporaryLinkCall
                        && useServerIdentityAsPrimary
                        && Boolean((event.remoteIdentityId || '').trim());

                      return (
                    <div
                      key={event.id}
                      className="w-full p-4 transition-colors hover:bg-white/70 sm:p-5"
                    >
                      <div className="flex items-start">
                        <button
                          onClick={() => handleToggleActions(event)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-base font-semibold text-primary-700">
                              {primaryDisplayName.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-base font-semibold text-[color:var(--text-strong)]" title={primaryDisplayName}>
                                {primaryDisplayName}
                              </div>
                              {directGuestBadge ? (
                                <div className="mt-1">
                                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-amber-700">
                                    {directGuestBadge}
                                  </span>
                                </div>
                              ) : null}
                              {showCompactIdentityId && (
                                <div className="mt-0.5 truncate text-xs text-[color:var(--text-soft)]" title={event.remoteIdentityId || ''}>
                                  {event.remoteIdentityId}
                                </div>
                              )}

                              <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[color:var(--text-muted)]">
                                {(() => {
                                  const outcome = getOutcome(event);
                                  const icon = renderOutcomeIcon(event);
                                  if (!icon) return null;

                                  const outcomeColorClass =
                                    outcome === 'declined'
                                      ? 'text-red-600'
                                      : outcome === 'missed'
                                        ? 'text-[color:var(--text-strong)]'
                                        : 'text-[color:var(--text-muted)]';

                                  return (
                                    <span className={outcomeColorClass} aria-hidden="true">
                                      {icon}
                                    </span>
                                  );
                                })()}
                                <span
                                  className={event.direction === 'incoming' ? 'text-green-600' : 'text-[color:var(--text-muted)]'}
                                  aria-hidden="true"
                                >
                                  {renderDirectionIcon(event.direction)}
                                </span>
                                <span>{formatTime(event.timestamp)}</span>
                                {callDuration && (
                                  <span className="rounded-full border border-[color:var(--border-soft)] bg-white/80 px-2 py-1 text-[11px] font-medium text-[color:var(--text-strong)]">
                                    {callDuration}
                                  </span>
                                )}
                                {serverLabel && (
                                  <span className="inline-flex min-w-0 items-center gap-1 rounded-full border border-[color:var(--border-soft)] bg-white/80 px-2 py-1 text-[11px]">
                                    <span
                                      className={`inline-block h-1.5 w-1.5 rounded-full ${resolveServerColorClass(event.serverId, serverColorById[event.serverId], 'dot')}`}
                                      aria-hidden="true"
                                    />
                                    <span className="truncate" title={serverLabel}>
                                      {serverLabel}
                                    </span>
                                  </span>
                                )}
                                {!isKnown && !isTemporaryLinkCall && (
                                  <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                                    {t('calls.unknownKey')}
                                  </span>
                                )}
                                {isTemporaryLinkCall && (
                                  <span className="rounded-full bg-purple-50 px-2 py-1 text-[11px] text-purple-700">
                                    {t('calls.temporaryLinkBadge')}
                                  </span>
                                )}
                              </div>

                              {!isKnown && displayName && (
                                <div className="mt-1 text-sm text-[color:var(--text-muted)]">
                                  {t('common.separatorDot')}
                                  {event.remoteIdentityId}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>

                        <div className="ml-3 flex shrink-0 items-start gap-2">
                          {!isKnown && !isTemporaryLinkCall && resolveServerUrlForEvent(event) && resolvePublicKeyForUnknownEvent(event)?.value && (
                            <button
                              onClick={() => {
                                handleAddUnknownToContacts(event);
                              }}
                              onMouseUp={releaseActionButtonFocus}
                              onTouchEnd={releaseActionButtonFocus}
                              className="btn btn-secondary h-11 w-11 p-0"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-label={t('common.addContact')}
                              title={t('common.addContact')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          )}
                          {isKnown && resolved && resolvedIdentityId && (
                            <button
                              onClick={() => {
                                void handleCallFromHistory(event, { withVideo: false });
                              }}
                              onMouseUp={releaseActionButtonFocus}
                              onTouchEnd={releaseActionButtonFocus}
                              disabled={!canRetryCall}
                              className={`btn btn-secondary h-11 w-11 p-0 ${
                                canRetryCall ? '' : 'opacity-50 cursor-not-allowed'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-label={t('common.call')}
                              title={retryDisabledTitle}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </button>
                          )}

                          {!isKnown && (
                            <button
                              onClick={() => {
                                void handleCallFromHistory(event, { withVideo: false });
                              }}
                              onMouseUp={releaseActionButtonFocus}
                              onTouchEnd={releaseActionButtonFocus}
                              disabled={!canRetryCall || busyEventId === event.id}
                              className={`btn btn-secondary h-11 w-11 p-0 ${
                                canRetryCall && busyEventId !== event.id ? '' : 'opacity-50 cursor-not-allowed'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-label={t('common.call')}
                              title={retryDisabledTitle}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      {isKnown && resolved && expandedEventId === event.id && (
                        <div className="mt-3 border-t border-[color:var(--border-soft)] pt-3">
                          <div className="flex items-center justify-around">
                            <button
                              type="button"
                              onClick={() => handleOpenHistoryItem(event)}
                              className="flex flex-col items-center gap-1 px-3 py-2 text-xs text-[color:var(--text-muted)] hover:text-[color:var(--text-strong)]"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span>{t('common.details')}</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (!resolvedIdentityId) return;
                                const localIdentityId = resolveLocalIdentityIdForEvent(event);
                                if (!localIdentityId) {
                                  navigate('/messages');
                                  return;
                                }
                                navigate(
                                  `/messages/${encodeURIComponent(localIdentityId)}/${encodeURIComponent(resolvedIdentityId)}`,
                                  { state: { from: 'calls' } }
                                );
                              }}
                              disabled={!resolvedIdentityId}
                              className={`flex flex-col items-center gap-1 px-3 py-2 text-xs text-[color:var(--text-muted)] hover:text-[color:var(--text-strong)] ${
                                resolvedIdentityId ? '' : 'opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5M21 12a9 9 0 11-4-7.5L21 12z" />
                              </svg>
                              <span>{t('common.write')}</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (!resolvedIdentityId) return;
                                if (isTemporaryLinkCall) {
                                  showWarning(t('calls.callbackBlockedTemporaryLink'));
                                  return;
                                }
                                void initiateCall(resolved.contactId, true, resolvedIdentityId);
                              }}
                              disabled={!resolvedIdentityId || isTemporaryLinkCall}
                              className={`flex flex-col items-center gap-1 px-3 py-2 text-xs text-[color:var(--text-muted)] hover:text-[color:var(--text-strong)] ${
                                resolvedIdentityId && !isTemporaryLinkCall ? '' : 'opacity-50 cursor-not-allowed'
                              }`}
                              title={isTemporaryLinkCall ? t('calls.callbackBlockedTemporaryLink') : t('common.videoCall')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14" />
                                <rect x="3" y="7" width="12" height="10" rx="2" ry="2" strokeWidth={2} />
                              </svg>
                              <span>{t('common.videoCall')}</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}
            </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
